'use client';

import React, { FC, useState, useEffect } from 'react';

import PromoSlotSitewideBanner from '@/components/promoSlot/PromoSlotSitewideBanner';
import { Image } from '@/components/ui';

import { PAGE_PATHS } from '@/core/constants';
import { TypeGlobalNavFields } from '@/core/generated-types';

import { useComponentLoading } from '@/hooks/useComponentLoading';
import useLocale from '@/hooks/useLocale';
import { useRestrictedCartMode } from '@/hooks/useRestrictedCartMode';

import { usePathname } from '@/navigation';

import { Language } from '@/utils/translations/types/TypeLanguagesAndLocales';
import { PAGE_LOCATIONS } from '@/utils/rudderstack';

import { LargeNav } from './LargeNav/LargeNav';
import { SmallNav } from './SmallNav/SmallNav';

export type NavComponentProps = Omit<
  TypeGlobalNavFields,
  'homeCta' | 'pklpCta'
> & {
  isSticky: boolean;
  hasLargeWordsStyle: boolean;
  buttonCta?: {
    link: TypeGlobalNavFields['homeCta'] | TypeGlobalNavFields['pklpCta'];
    pageLocation: PAGE_LOCATIONS;
  };
};

const getButtonCta = (
  pathname: string,
  homeCta: TypeGlobalNavFields['homeCta'],
  pklpCta: TypeGlobalNavFields['pklpCta'],
) => {
  if (pathname === '/') {
    return { link: homeCta, pageLocation: PAGE_LOCATIONS.NAVIGATION };
  }

  if (pklpCta && pathname === PAGE_PATHS.THE_PLAY_KITS_PAGE) {
    return { link: pklpCta, pageLocation: PAGE_LOCATIONS.PLAY_KITS_PAGE };
  }

  if (pklpCta && pathname.includes('/products/the-play-kits/')) {
    return { link: pklpCta, pageLocation: PAGE_LOCATIONS.PLAY_KITS_AGE_BASED };
  }

  return undefined;
};

const GlobalNav: FC<TypeGlobalNavFields> = ({ homeCta, pklpCta, ...props }) => {
  const pathname = usePathname();

  const locale = useLocale();

  const hasLargeWordsStyle = [
    Language.German,
    Language.Dutch,
    Language.Spanish,
    Language.French,
  ].includes(locale as Language);

  const buttonCta = getButtonCta(pathname, homeCta, pklpCta);

  const [isScrolled, setIsScrolled] = useState(false);

  const isRestrictedCart = useRestrictedCartMode();

  useEffect(() => {
    const onScroll = () => {
      setIsScrolled(window.scrollY > 200);
    };

    window.addEventListener('scroll', onScroll);

    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // This is a workaround to prevent hydration errors due to client side loading
  const componentLoading = useComponentLoading();

  if (componentLoading) {
    return null;
  }

  // In restricted cart mode, only show logo without making it clickable
  if (isRestrictedCart && props.logo) {
    return (
      <nav className="bg-white border-b border-grey-mid-light sticky top-0 z-50">
        {/* Desktop view */}
        <div className="hidden lg:flex justify-center items-center h-[80px] px-4">
          <div className="w-[150px] xl:w-[166px] pointer-events-none">
            <Image imageData={props.logo} alt={props.logo.fields.altText} />
          </div>
        </div>

        {/* Mobile view */}
        <div className="flex lg:hidden justify-center items-center h-[64px] px-4">
          <div className="w-[120px] pointer-events-none">
            <Image imageData={props.logo} alt={props.logo.fields.altText} />
          </div>
        </div>
      </nav>
    );
  }

  return (
    <>
      <PromoSlotSitewideBanner {...props} />

      <LargeNav
        {...props}
        isSticky={isScrolled}
        buttonCta={buttonCta}
        hasLargeWordsStyle={hasLargeWordsStyle}
      />

      <SmallNav
        {...props}
        isSticky={isScrolled}
        buttonCta={buttonCta}
        hasLargeWordsStyle={hasLargeWordsStyle}
      />
    </>
  );
};

export default React.memo(GlobalNav);
